import { useError } from "contexts/ErrorContext";
import useApi from "hooks/useGaneshApi";
import React, { useEffect, useState } from "react";

import {
  FulfillmentVolumesRecord_T,
  VolumeMetricsKeys,
} from "types/FulfillmentsAnalytics";

import GaneshDataGrid from "components/DataGrid/GaneshDataGrid";

import {
  Box,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts-pro";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";

import { useQuery } from "@tanstack/react-query";
import { Granularity_T } from "types/Analytics";
import {
  getEndOfCurrentReportPeriod,
  GRANULARITY,
  granularityOptions,
} from "utils/analytics";
import { formatNumber } from "utils/formatting";
type ChartDataRecord = {
  x: string;
  y: number | null;
};

// Date constraints
const getMinDate = () => "2023-01-01"; //bound beginning of date selector to 2023 since that's when we started tracking fulfillment volumes

// Default date values
const getDefaultStartDate = () => {
  // default start date to beginning of previous year
  const currentYear = new Date().getFullYear();
  return `${currentYear - 1}-01-01`;
};

const getDefaultEndDate = () => {
  // default end date to end of current year
  const currentYear = new Date().getFullYear();
  return `${currentYear}-12-31`;
};

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "id", sort: "desc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const FulfillmentsDeliveredStats: React.FC<any> = () => {
  const [granularity, setGranularity] = useState<Granularity_T>(
    GRANULARITY.quarterly
  );
  const [chartView, setChartView] = useState<string | null>("customers_view");
  const [includeProjections, setIncludeProjections] = useState<string>("no");

  const [dateStart, setDateStart] = useState<string>(getDefaultStartDate());
  const [dateEnd, setDateEnd] = useState<string>(getDefaultEndDate());

  // Calculate end of current report period based on granularity
  const currentReportPeriodEnd = getEndOfCurrentReportPeriod(granularity.value);

  const [chartData, setChartData] = useState<Record<string, ChartDataRecord[]>>(
    {}
  );

  const { showError } = useError();
  const { apiRequest } = useApi();

  // React Query for fetching volumes data
  const {
    data: volumesData = [],
    isLoading,
    isError,
    error,
  } = useQuery<FulfillmentVolumesRecord_T[], Error>({
    queryKey: [
      "fulfillment-delivered",
      granularity.value,
      dateStart,
      dateEnd,
      includeProjections === "yes",
    ],
    queryFn: async () => {
      const response = await apiRequest<FulfillmentVolumesRecord_T[]>(
        `/v1/fulfillments_analytics/delivered?dateStart=${dateStart}&dateEnd=${dateEnd}&granularity=${granularity.value}`
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
  });

  if (isError) {
    showError(`Failed to fetch fulfillment volumes: ${error?.message}`);
  }

  // Prepare data for chart
  const prepareChartData = (
    records: FulfillmentVolumesRecord_T[],
    keys: VolumeMetricsKeys[]
  ) => {
    const chartData: Record<string, ChartDataRecord[]> = {};

    keys.forEach((key) => {
      chartData[key] = records.map((item) => ({
        x: item.fulfillment_date_period || "",
        y: item[key] ?? null,
      }));
    });

    return { chartData };
  };

  // Filter data based on includeProjections setting
  const getFilteredData = () => {
    if (includeProjections === "yes") {
      return volumesData;
    } else {
      // if projections are off, filter data to only include data up to the current report period
      return volumesData.filter((item) => {
        const itemDate = item.max_fulfillment_date;
        const itemDateObj = new Date(itemDate);
        const cutoffDateObj = new Date(currentReportPeriodEnd);
        return itemDate && itemDateObj <= cutoffDateObj;
      });
    }
  };

  useEffect(() => {
    // Choose keys based on includeProjections setting
    const keys: VolumeMetricsKeys[] =
      includeProjections === "yes"
        ? [
            "count_customers_completed",
            "count_fulfillments_completed",
            "count_volume_upgrades_completed",
            "count_customers_planned",
            "count_fulfillments_planned",
            "count_volume_upgrades_planned",
          ]
        : [
            "count_customers_completed",
            "count_fulfillments_completed",
            "count_volume_upgrades_completed",
          ];

    if (volumesData.length > 0) {
      const filteredData = getFilteredData();
      const { chartData } = prepareChartData(filteredData, keys);
      setChartData(chartData);
    } else {
      setChartData({});
    }
  }, [volumesData, includeProjections, currentReportPeriodEnd]);

  const handleChartViewToggle = (
    event: React.MouseEvent<HTMLElement>,
    newView:
      | "customers_view"
      | "fulfillments_view"
      | "volume_upgrades_view"
      | null
  ) => {
    if (newView) setChartView(newView);
  };

  const handleGranularityChange = (event: any) => {
    const selectedOption = granularityOptions.find(
      (option) => option.label === event.target.value
    );
    if (selectedOption) {
      setGranularity(selectedOption);
    }
  };

  const getChartTitle = () => {
    const timePeriod = granularity.label;
    const viewLabel =
      chartView === "customers_view"
        ? "Customers"
        : chartView === "fulfillments_view"
        ? "Fulfillments"
        : "Volume Upgrades";

    const projectionText =
      includeProjections === "yes" ? "with Projections" : "Delivered";

    return `${timePeriod} ${viewLabel} ${projectionText}`;
  };

  const renderChart = () => {
    const selectedDataKey =
      chartView === "customers_view"
        ? "count_customers_completed"
        : chartView === "fulfillments_view"
        ? "count_fulfillments_completed"
        : "count_volume_upgrades_completed";

    if (isLoading) {
      return <CircularProgress />;
    }

    // Determine chart configuration based on current state
    let dataset: any[] = [];
    let series: any[] = [];
    let xAxisKey = "period";
    let hasData = false;

    if (includeProjections === "yes") {
      const completedKey = selectedDataKey;
      const plannedKey = selectedDataKey.replace("_completed", "_planned");

      // Regular stacked chart (no YoY)
      const completedData = chartData[completedKey] || [];
      const plannedData = chartData[plannedKey] || [];

      if (completedData.length > 0 || plannedData.length > 0) {
        dataset = completedData.map((item, index) => {
          const plannedValue = plannedData[index]?.y || 0;
          const completedValue = item.y || 0;

          return {
            period: item.x,
            completed: completedValue,
            planned: plannedValue,
            total: completedValue + plannedValue,
          };
        });

        series = [
          {
            dataKey: "completed",
            label: "Completed",
            stack: "main",
            color: "#1976d2",
          },
          {
            dataKey: "planned",
            label: "Planned",
            stack: "main",
            color: "#42a5f5",
          },
        ];

        hasData = true;
      }
    } else {
      // Regular bar chart for completed only
      const chartDataset = chartData[selectedDataKey];

      if (chartDataset && chartDataset.length > 0) {
        dataset = chartDataset;
        series = [{ dataKey: "y" }];
        xAxisKey = "x";
        hasData = true;
      }
    }

    if (!hasData) {
      return <Typography>No data available</Typography>;
    }

    return (
      <BarChart
        dataset={dataset}
        xAxis={[
          {
            scaleType: "band",
            dataKey: xAxisKey,
            tickPlacement: "middle",
          },
        ]}
        series={series}
        tooltip={{
          trigger: "axis",
        }}
        slotProps={{
          legend: {
            direction: "row",
            position: {
              vertical: "top",
              horizontal: "middle",
            },
          },
        }}
        borderRadius={9}
        height={500}
      />
    );
  };

  const getColumnsDef = (): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      {
        field: "id",
        headerName: granularity.label,
        width: 150,
        renderCell: (params: GridRenderCellParams) =>
          params.row.fulfillment_date_period,
      },
    ];

    if (includeProjections === "yes") {
      // Show completed, planned, and totals when projections are included
      return [
        ...baseColumns,
        {
          field: "count_customers_completed",
          headerName: "Customers Completed",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_customers_planned",
          headerName: "Customers Additionally Planned",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_customers_total",
          headerName: "Customers Total",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_fulfillments_completed",
          headerName: "Fulfillments Completed",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_fulfillments_planned",
          headerName: "Fulfillments Planned",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_fulfillments_total",
          headerName: "Fulfillments Total",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_volume_upgrades_completed",
          headerName: "Volume Upgrades Completed",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_volume_upgrades_planned",
          headerName: "Volume Upgrades Planned",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_volume_upgrades_total",
          headerName: "Volume Upgrades Total",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
      ];
    } else {
      // Show only completed data when projections are not included
      return [
        ...baseColumns,
        {
          field: "count_customers_completed",
          headerName: "Customers Completed",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_fulfillments_completed",
          headerName: "Fulfillments Completed",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
        {
          field: "count_volume_upgrades_completed",
          headerName: "Volume Upgrades Completed",
          width: 150,
          renderCell: (params: GridRenderCellParams<Number>) =>
            formatNumber(params.value),
        },
      ];
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            marginBottom: "20px",
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            inputProps={{
              min: getMinDate(),
            }}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{ width: "150px" }}
          />
          <TextField
            label="End Date"
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            inputProps={{
              min: getMinDate(),
            }}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{ width: "150px" }}
          />

          {/* Granularity Dropdown */}
          <FormControl size="small">
            <InputLabel id="select-granularity-label">Granularity</InputLabel>
            <Select
              labelId="select-granularity-label"
              label="Granularity"
              value={granularity.label}
              onChange={handleGranularityChange}
              sx={{ width: "150px" }}
            >
              {granularityOptions.map((option) => (
                <MenuItem key={option.value} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Include Projections Dropdown */}
          <FormControl size="small">
            <InputLabel id="select-projections-label">
              Include Projections
            </InputLabel>
            <Select
              labelId="select-projections-label"
              label="Include Projections"
              value={includeProjections}
              onChange={(e) => setIncludeProjections(e.target.value as string)}
              sx={{ width: "150px" }}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Grid>

      {/* Chart Section */}
      <Grid item xs={12}>
        <Card className="p-0" style={{ padding: "20px" }}>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "35px",
            }}
          >
            <ToggleButtonGroup
              color="primary"
              value={chartView}
              exclusive
              onChange={handleChartViewToggle}
              aria-label="chart-view"
            >
              <ToggleButton
                value="customers_view"
                aria-label="customers_view"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Customers</Typography>
              </ToggleButton>
              <ToggleButton
                value="fulfillments_view"
                aria-label="fulfillments_view"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Fulfillments</Typography>
              </ToggleButton>
              <ToggleButton
                value="volume_upgrades_view"
                aria-label="volume_upgrades_view"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Volume Upgrades</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Chart Title */}
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              marginBottom: "20px",
              fontWeight: "bold",
            }}
          >
            {getChartTitle()}
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            {renderChart()}
          </Box>
        </Card>
      </Grid>

      {/* Data Grid Section */}
      <Grid item xs={12}>
        <GaneshDataGrid
          loading={isLoading}
          data={getFilteredData()}
          columns={getColumnsDef()}
          gridId="historicallyCompletedStatsGrid"
          defaultGridState={defaultGridState}
          customHeight="550px"
          showGridSaver={false}
        />
      </Grid>
    </Grid>
  );
};

export default FulfillmentsDeliveredStats;
